import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
    Box,
    Dialog,
    DialogContent,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import LoadingRow from './LoadingRow';
import { useTranslation } from 'react-i18next';
import {
    CHECKBOX_COLUMN_WIDTH,
    DEFAULT_COLUMN_WIDTH,
    DEFAULT_TITLE_SIZE,
    ICON_ACTION_COLUMN_WIDTH,
    INDEX_COLUMN_WIDTH,
} from 'utils/constants';
import { getMessageText } from 'utils/helpers';
import { TKeys } from 'utils/enums';
import AppCheckbox from '../Checkbox/AppCheckbox';
import TableIcon from './table-icon';
import SortIcon from './sort-icon';
import {
    ContentSubTableCell,
    ContentTableCell,
    FreezeWidgetCell,
    HeaderContent,
    HeaderIconCell,
    HeaderTitle,
    PaginationBox,
    PaginationButtons,
    PaginationText,
    StyledHeaderCell,
} from './styled';
import { SettingIcon } from 'assets/icons';
import { AppDialogTitle } from '../dialog/dialog-title';
import DialogButtons from '../dialog/dialog-buttons';

const DataGridView: React.FC<DataGridViewProps> = ({
    columns,
    itemCount,
    cellBuilder,
    selectedRows = [],
    selectedCallback,
    showIndex = true,
    sortListener,
    tapListener,
    sortColumnIndex = -1,
    ascending = true,
    enablePagination = false,
    loadMoreCallback,
    onColumnDragEnd,
    onRowDragEnd,
    tapLinkListener,
    tapIconActionListener,
    pageChangeCallBack,
    paginateMetaData,
    widgetSub,
    widthWidgetSub,
    isCurrentViewDeletedRecord,
    isFreezeWidgetSub,
    isDisableDragRow = true,
    isVisibleDeletedItems = false,
    revertIconTapped,
    defaultColumns = [],
    settingColumnCallback,
    isVisibleActionIcon = false,
    fitContentHeight = false,
}) => {
    const { t } = useTranslation();
    // Local state for row selection
    const [localSelectedRows, setLocalSelectedRows] = useState<number[]>(selectedRows);

    const [settingColumnDialog, setSettingColumnDialog] = useState<IAppDialog<DataGridColumn[]>>({
        status: false,
        data: defaultColumns,
    });

    useEffect(() => {
        setLocalSelectedRows(selectedRows);
    }, [selectedRows]);

    // Maintain local column order state.
    const [columnOrder, setColumnOrder] = useState<DataGridColumn[]>(columns);
    useEffect(() => {
        setColumnOrder(columns);
    }, [columns]);

    useEffect(() => {
        setLocalSelectedRows(selectedRows);
    }, [selectedRows]);

    // Maintain local row order state.
    const [rowOrder, setRowOrder] = useState<number[]>(() =>
        Array.from({ length: itemCount }, (_, i) => i),
    );
    useEffect(() => {
        setRowOrder(() => Array.from({ length: itemCount }, (_, i) => i));
    }, [itemCount]);

    // Refs to store the index of dragged column/row
    const dragColIndex = useRef<number | null>(null);
    const dragRowIndex = useRef<number | null>(null);

    const totalCellWidth = columnOrder.reduce(
        (sum, col) => sum + (col.width || DEFAULT_COLUMN_WIDTH),
        0,
    );
    const gridWidth =
        CHECKBOX_COLUMN_WIDTH + (showIndex ? totalCellWidth + INDEX_COLUMN_WIDTH : totalCellWidth);

    const handleSortChange = (columnIndex: number) => {
        if (sortListener) {
            if (sortColumnIndex === columnIndex) {
                sortListener(columnIndex, !ascending);
            } else {
                sortListener(columnIndex, true);
            }
        }
    };

    const handleRowSelection = (rowIndex: number) => {
        const updatedSelection = localSelectedRows.includes(rowIndex)
            ? localSelectedRows.filter((index) => index !== rowIndex)
            : [...localSelectedRows, rowIndex];

        setLocalSelectedRows(updatedSelection);
        selectedCallback?.(updatedSelection);
    };

    const handleSelectAll = () => {
        const filteredRowOrder = rowOrder?.filter((index) => {
            const cellData = cellBuilder(index);
            const disableCheckBox =
                cellData?.find((cell: any) => typeof cell.disableCheckBox === 'boolean')
                    ?.disableCheckBox ?? false;

            return !disableCheckBox;
        });

        const allSelected = localSelectedRows.length === filteredRowOrder.length;

        const updatedSelection = allSelected ? [] : [...filteredRowOrder];

        setLocalSelectedRows(updatedSelection);
        selectedCallback?.(updatedSelection);
    };

    // --- Column Drag Handlers ---
    const onColumnDragStart = (event: React.DragEvent<HTMLTableCellElement>, index: number) => {
        dragColIndex.current = index;
        event.dataTransfer.effectAllowed = 'move';
    };

    const onColumnDragOver = (event: React.DragEvent<HTMLTableCellElement>) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
    };

    const onColumnDrop = (event: React.DragEvent<HTMLTableCellElement>, dropIndex: number) => {
        event.preventDefault();
        const dragIndex = dragColIndex.current;
        if (dragIndex === null || dragIndex === dropIndex) return;
        const newOrder = Array.from(columnOrder);
        const [movedColumn] = newOrder.splice(dragIndex, 1);
        newOrder.splice(dropIndex, 0, movedColumn);
        setColumnOrder(newOrder);
        onColumnDragEnd?.(newOrder);
        dragColIndex.current = null;
    };

    // --- Row Drag Handlers ---
    const onRowDragStart = (event: React.DragEvent<HTMLTableRowElement>, index: number) => {
        dragRowIndex.current = index;
        event.dataTransfer.effectAllowed = 'move';
    };

    const onRowDragOver = (event: React.DragEvent<HTMLTableRowElement>) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
    };

    const onRowDrop = (event: React.DragEvent<HTMLTableRowElement>, dropIndex: number) => {
        if (isDisableDragRow) return;
        event.preventDefault();
        const dragIndex = dragRowIndex.current;
        if (dragIndex === null || dragIndex === dropIndex) return;
        const newRowOrder = Array.from(rowOrder);
        const [movedRow] = newRowOrder.splice(dragIndex, 1);
        newRowOrder.splice(dropIndex, 0, movedRow);
        setRowOrder(newRowOrder);
        onRowDragEnd?.(newRowOrder);
        dragRowIndex.current = null;
    };

    const isShowPagination =
        pageChangeCallBack && paginateMetaData?.total && paginateMetaData?.total > 0;
    const extraColumns = (tapIconActionListener ? 1 : 0) + (widgetSub ? 1 : 0);
    const colSpan = columnOrder.length + 1 + extraColumns;

    const currentPage = paginateMetaData?.page ?? 1;
    const itemsPerPage = paginateMetaData?.per_page ?? 100;
    const totalItems = paginateMetaData?.total ?? 1;
    const totalPages = paginateMetaData?.total_pages ?? 1;

    const startIndex = (currentPage - 1) * itemsPerPage + 1;
    const endIndex = Math.min(startIndex + itemsPerPage - 1, totalItems);

    const txtPagination = getMessageText(t(TKeys.LBL000001), totalItems, startIndex, endIndex);

    const _handleShowSettingColumns = () => {
        setSettingColumnDialog({
            status: true,
            data: defaultColumns,
        });
    };

    const _renderSettingIcon = () => {
        return (
            <IconButton aria-label="Setting" onClick={_handleShowSettingColumns}>
                <SettingIcon />
            </IconButton>
        );
    };

    const _handleCloseSettingColumnDialog = () => {
        setSettingColumnDialog({
            status: false,
        });
    };

    const disabledCheckboxAll = useMemo(
        () =>
            rowOrder.every(
                (_, displayIndex) =>
                    cellBuilder(displayIndex).find((i: any) => i?.disableCheckBox)
                        ?.disableCheckBox ?? false,
            ),
        [rowOrder, cellBuilder],
    );

    const _renderSettingColumnDialog = () => {
        return (
            <Dialog
                open={settingColumnDialog.status}
                onClose={(_, reason) => {
                    if (reason === 'backdropClick') return;
                    _handleCloseSettingColumnDialog();
                }}
                disableEscapeKeyDown
                autoFocus
                maxWidth="sm"
                fullWidth>
                <AppDialogTitle title={t(TKeys.LBL020010)} />

                <DialogContent>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(1, 1fr)',
                        }}>
                        {settingColumnDialog.data?.map((item, i) => (
                            <AppCheckbox
                                key={i}
                                checked={!item.invisible}
                                onChange={(e) => {
                                    const newData = [...(settingColumnDialog.data ?? [])];
                                    const index = newData.findIndex((e) => e.field === item.field);
                                    if (index !== -1) {
                                        newData[index] = {
                                            ...newData[index],
                                            invisible: !e.target.checked,
                                        };
                                        setSettingColumnDialog({
                                            ...settingColumnDialog,
                                            data: newData,
                                        });
                                    }
                                }}
                                label={t(item.title)}
                            />
                        ))}
                    </Box>
                </DialogContent>

                <DialogButtons
                    onClose={_handleCloseSettingColumnDialog}
                    onSubmit={() => {
                        if (settingColumnCallback) {
                            settingColumnCallback(settingColumnDialog.data ?? []);
                        }

                        _handleCloseSettingColumnDialog();
                    }}
                    cancelLabel={t(TKeys.BTN000016)}
                    submitLabel={t(TKeys.BTN000017)}
                />
            </Dialog>
        );
    };

    return (
        <>
            <TableContainer
                component={Paper}
                sx={{
                    overflow: fitContentHeight ? 'visible' : 'auto',
                    height: fitContentHeight ? 'auto' : '100%',
                }}>
                <Table sx={{ tableLayout: 'auto', minWidth: gridWidth }}>
                    <TableHead sx={{ position: 'sticky', top: 0, zIndex: 10, height: 55 }}>
                        <TableRow sx={{ backgroundColor: 'var(--color-2281cc)' }}>
                            {/* Header Checkbox for select all */}
                            {!isVisibleDeletedItems && (
                                <TableCell sx={{ width: CHECKBOX_COLUMN_WIDTH }}>
                                    <AppCheckbox
                                        checked={
                                            localSelectedRows.length === rowOrder.length &&
                                            rowOrder.length > 0
                                        }
                                        indeterminate={
                                            localSelectedRows.length > 0 &&
                                            localSelectedRows.length < rowOrder.length
                                        }
                                        onChange={
                                            isCurrentViewDeletedRecord ? undefined : handleSelectAll
                                        }
                                        disabled={isCurrentViewDeletedRecord || disabledCheckboxAll}
                                    />
                                </TableCell>
                            )}
                            {showIndex && (
                                <TableCell sx={{ width: INDEX_COLUMN_WIDTH, fontWeight: 'bold' }} />
                            )}
                            {columnOrder.map((column, colIndex) => (
                                <StyledHeaderCell
                                    key={colIndex}
                                    draggable
                                    isSortable={!!sortListener}
                                    align={column.titleAlign || 'left'}
                                    onDragStart={(e) => onColumnDragStart(e, colIndex)}
                                    onDragOver={onColumnDragOver}
                                    onDrop={(e) => onColumnDrop(e, colIndex)}
                                    onClick={() => sortListener && handleSortChange(colIndex)}
                                    sx={{
                                        fontSize: column.titleSize || DEFAULT_TITLE_SIZE,
                                    }}>
                                    <HeaderContent>
                                        <HeaderTitle>{t(column.title)}</HeaderTitle>
                                        {sortListener && sortColumnIndex === colIndex && (
                                            <SortIcon isUp={ascending} />
                                        )}
                                    </HeaderContent>
                                </StyledHeaderCell>
                            ))}
                            {widgetSub && (
                                <FreezeWidgetCell
                                    isFreeze={!!isFreezeWidgetSub}
                                    sx={{ width: widthWidgetSub ?? ICON_ACTION_COLUMN_WIDTH }}
                                />
                            )}
                            {tapIconActionListener && <HeaderIconCell />}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {itemCount > 0 ? (
                            rowOrder.map((actualRowIndex, displayIndex) => {
                                // Pass the display index (not the actualRowIndex) to cellBuilder
                                const cellData = cellBuilder(displayIndex);

                                // Disable  edit when cell have disableEdit is true
                                const disableEdit =
                                    cellData.find(
                                        (cell: any) => typeof cell.disableEdit === 'boolean',
                                    )?.disableEdit ?? false;

                                // Disable  checkbox when cell have disableCheckBox is true
                                const disableCheckBox =
                                    cellData.find(
                                        (cell: any) => typeof cell.disableCheckBox === 'boolean',
                                    )?.disableCheckBox ?? false;

                                return (
                                    <TableRow
                                        key={actualRowIndex}
                                        draggable={false}
                                        onDragStart={(e) => onRowDragStart(e, displayIndex)}
                                        onDragOver={onRowDragOver}
                                        onDrop={(e) => onRowDrop(e, displayIndex)}
                                        onClick={() =>
                                            !tapLinkListener && tapListener?.(actualRowIndex)
                                        }
                                        sx={{
                                            backgroundColor:
                                                displayIndex % 2 === 0 ? 'white' : '#f5f5f5',
                                            cursor: 'default',
                                        }}>
                                        {/* Row selection checkbox */}
                                        {!isVisibleDeletedItems && (
                                            <TableCell sx={{ width: CHECKBOX_COLUMN_WIDTH }}>
                                                <AppCheckbox
                                                    checked={localSelectedRows.includes(
                                                        actualRowIndex,
                                                    )}
                                                    onChange={() =>
                                                        handleRowSelection(actualRowIndex)
                                                    }
                                                    disabled={disableCheckBox}
                                                />
                                            </TableCell>
                                        )}
                                        {showIndex && (
                                            <TableCell
                                                sx={{
                                                    width: INDEX_COLUMN_WIDTH,
                                                    textAlign: 'center',
                                                }}>
                                                {actualRowIndex + 1}
                                            </TableCell>
                                        )}
                                        {(() => {
                                            if (cellData.length !== columnOrder.length) {
                                                throw new Error(
                                                    'cellBuilder returned an incorrect number of cells',
                                                );
                                            }

                                            return cellData.map((cell: any, cellIndex: any) => {
                                                const columnDataType =
                                                    columnOrder[cellIndex]?.columnDataType;

                                                return (
                                                    <ContentTableCell
                                                        key={cellIndex}
                                                        isLink={
                                                            `${cell.content}` !== '' &&
                                                            columnDataType === 'link'
                                                        }
                                                        sx={{
                                                            maxWidth:
                                                                columnOrder[cellIndex]?.width ||
                                                                DEFAULT_COLUMN_WIDTH,
                                                        }}
                                                        onClick={() =>
                                                            columnDataType === 'link' &&
                                                            tapLinkListener &&
                                                            tapLinkListener(actualRowIndex)
                                                        }>
                                                        {cell.content}
                                                    </ContentTableCell>
                                                );
                                            });
                                        })()}
                                        {/* widget sub action */}
                                        {widgetSub && (
                                            <ContentSubTableCell
                                                isFrozen={!!isFreezeWidgetSub}
                                                isEvenRow={displayIndex % 2 === 0}>
                                                {widgetSub(actualRowIndex)}
                                            </ContentSubTableCell>
                                        )}
                                        {/* Row action icon */}
                                        {(!isVisibleDeletedItems || isVisibleActionIcon) &&
                                            tapIconActionListener && (
                                                <TableIcon
                                                    type="edit"
                                                    index={displayIndex}
                                                    disabled={
                                                        isCurrentViewDeletedRecord || disableEdit
                                                    }
                                                    onTapped={() =>
                                                        tapIconActionListener &&
                                                        !isCurrentViewDeletedRecord &&
                                                        tapIconActionListener(actualRowIndex)
                                                    }
                                                />
                                            )}
                                        {isVisibleDeletedItems && revertIconTapped && (
                                            <TableIcon
                                                type="revert"
                                                index={displayIndex}
                                                disabled={false}
                                                onTapped={() =>
                                                    revertIconTapped &&
                                                    revertIconTapped(actualRowIndex)
                                                }
                                            />
                                        )}
                                    </TableRow>
                                );
                            })
                        ) : (
                            <TableRow>
                                <TableCell align="center" height={44} colSpan={colSpan}>
                                    <Box
                                        display="flex"
                                        flexDirection="row"
                                        justifyContent="center"
                                        alignItems="center">
                                        <Typography
                                            sx={{ fontSize: '1rem', color: 'var(--color-black)' }}>
                                            {t('LBL130009')}
                                        </Typography>
                                        {_renderSettingIcon()}
                                    </Box>
                                </TableCell>
                            </TableRow>
                        )}
                        {enablePagination && (
                            <LoadingRow
                                key="loading"
                                loadMoreCallback={loadMoreCallback}
                                totalWidth={gridWidth}
                                colSpan={
                                    showIndex ? columnOrder.length + 2 : columnOrder.length + 1
                                }
                            />
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {!!isShowPagination && (
                <Box
                    display="flex"
                    flexDirection="row"
                    justifyContent="flex-end"
                    alignItems="center">
                    {_renderSettingIcon()}

                    <PaginationBox>
                        <PaginationText>{txtPagination}</PaginationText>
                        <PaginationButtons
                            page={currentPage}
                            onChange={(_, currPage) => pageChangeCallBack(currPage)}
                            count={totalPages}
                            variant="outlined"
                            shape="rounded"
                        />
                    </PaginationBox>
                </Box>
            )}

            {_renderSettingColumnDialog()}
        </>
    );
};

export { DataGridView };
